/**
 * H3.2 — ApprovedActionsManager
 * CRUD UI for client_approved_actions inside the Kund 360 "Knappar & länkar" tab.
 *
 * Lets admin:
 *   • see every per-client action with status + linked badge + runtime_eligible state
 *   • create new actions (URL validated server-side, rejects javascript:/data:/file:)
 *   • edit label, URL, render_as, status
 *   • link / unlink an action to the blueprint.approved_action_ids list
 *   • see a live preview of how the CTA would render in the widget
 *
 * No delete in v1 — admin uses status=disabled to turn an action off.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Save, RefreshCw, ExternalLink, Link as LinkIcon, Unlink,
  CheckCircle2, AlertCircle, XCircle, Loader2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

const STATUS_OPTIONS = [
  { value: "suggested",       label: "Förslag (suggested)" },
  { value: "review_required", label: "Granska (review_required)" },
  { value: "approved",        label: "Godkänd (approved)" },
  { value: "disabled",        label: "Inaktiverad (disabled)" },
];

const RENDER_OPTIONS = [
  { value: "primary_button",   label: "Primär knapp" },
  { value: "secondary_button", label: "Sekundär knapp" },
  { value: "link_card",        label: "Länkkort" },
];

const URL_SCHEME_HELP = (
  "Tillåtet: https:// · http:// · mailto: · tel: · /relativ-path · #anchor. " +
  "Blockerat: javascript:, data:, vbscript:, file:."
);

function isSafeUrl(url) {
  if (!url) return true;       // empty is allowed (status=suggested)
  if (/^(javascript|data|vbscript|file):/i.test(url)) return false;
  return /^(https?:\/\/|mailto:|tel:|\/|#)/i.test(url);
}

function StatusBadge({ value }) {
  const palette = {
    approved:        { bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-200" },
    suggested:       { bg: "bg-slate-50",    text: "text-slate-600",   border: "border-slate-200" },
    review_required: { bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-200" },
    disabled:        { bg: "bg-gray-100",    text: "text-gray-500",    border: "border-gray-200" },
  }[value] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200" };
  return (
    <span
      className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${palette.bg} ${palette.text} ${palette.border}`}
      data-testid={`action-status-${value}`}
    >
      {value}
    </span>
  );
}

function ActionPreview({ action }) {
  if (!action.url) {
    return <span className="text-xs text-gray-400 italic">Ingen URL → ingen preview</span>;
  }
  if (action.render_as === "primary_button") {
    return (
      <a
        href={action.url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F2A30] text-white text-xs font-medium rounded-full hover:bg-[#255E69] transition-colors"
      >
        {action.label_override || action.action_id}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  if (action.render_as === "link_card") {
    return (
      <a
        href={action.url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs hover:border-[#255E69] transition-colors max-w-full"
      >
        <ExternalLink className="w-3.5 h-3.5 text-[#255E69] shrink-0" />
        <span className="truncate">{action.label_override || action.action_id}</span>
      </a>
    );
  }
  return (
    <a
      href={action.url} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#255E69]/30 text-[#255E69] text-xs font-medium rounded-full hover:bg-[#255E69]/5 transition-colors"
    >
      {action.label_override || action.action_id}
      <ExternalLink className="w-3 h-3" />
    </a>
  );
}

function NewActionForm({ clientId, onCreated, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [actionId, setActionId] = useState("");
  const [templateId, setTemplateId] = useState("contact_form");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [renderAs, setRenderAs] = useState("secondary_button");
  const [status, setStatus] = useState("approved");
  const [evidence, setEvidence] = useState("");
  const [saving, setSaving] = useState(false);

  const urlError = !isSafeUrl(url) ? "Ogiltigt URL-format" : "";
  const actionIdError = actionId && !/^[a-z][a-z0-9_]{2,139}$/.test(actionId)
    ? "Måste vara snake_case, 3-140 tecken, börja på bokstav"
    : "";
  const canSave = !!actionId && !actionIdError && !urlError && !saving;

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({
            action_id: actionId.trim(),
            template_id: templateId.trim() || "custom",
            label_override: label.trim() || undefined,
            url: url.trim() || undefined,
            render_as: renderAs,
            status,
            evidence: evidence.trim() || undefined,
          }),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || data.error || "Misslyckades");
      toast.success(`Action "${actionId}" skapad`);
      onCreated?.(data.action);
    } catch (e) {
      toast.error(e.message || "Kunde inte skapa action");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#255E69]/30 bg-[#255E69]/5 rounded-lg p-4 space-y-3" data-testid="action-new-form">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-[#255E69]">Ny knapp / länk</h4>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700">Stäng</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <Label htmlFor="ai-action-id" className="text-xs">action_id (snake_case)</Label>
          <Input
            id="ai-action-id"
            value={actionId}
            onChange={(e) => setActionId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
            placeholder="book_call"
            data-testid="action-new-id"
          />
          {actionIdError && <p className="text-[11px] text-red-600 mt-1">{actionIdError}</p>}
        </div>
        <div>
          <Label htmlFor="ai-template-id" className="text-xs">template_id</Label>
          <Input
            id="ai-template-id"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="contact_form"
            data-testid="action-new-template"
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="ai-label" className="text-xs">Etikett (visas i widgeten)</Label>
          <Input
            id="ai-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Kontakta oss"
            data-testid="action-new-label"
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="ai-url" className="text-xs">URL</Label>
          <Input
            id="ai-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://elido.nu/#kontakt"
            data-testid="action-new-url"
          />
          <p className="text-[11px] text-gray-500 mt-1">{URL_SCHEME_HELP}</p>
          {urlError && <p className="text-[11px] text-red-600 mt-1">{urlError}</p>}
        </div>
        <div>
          <Label className="text-xs">Render-stil</Label>
          <select
            value={renderAs}
            onChange={(e) => setRenderAs(e.target.value)}
            className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            data-testid="action-new-render"
          >
            {RENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
            data-testid="action-new-status"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Evidens / källa (frivillig anteckning)</Label>
          <Textarea
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            rows={2}
            placeholder="ex. scrapad från /kontakt"
            data-testid="action-new-evidence"
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="action-new-cancel">Avbryt</Button>
        <Button size="sm" onClick={save} disabled={!canSave} data-testid="action-new-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Skapa
        </Button>
      </div>
    </div>
  );
}

function ActionRow({ action, clientId, onChanged }) {
  const { getAuthHeaders } = useAuth();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(action.label || action.label_override || "");
  const [url, setUrl] = useState(action.url || "");
  const [renderAs, setRenderAs] = useState(action.render_as || "secondary_button");
  const [status, setStatus] = useState(action.status || "suggested");
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    setLabel(action.label || action.label_override || "");
    setUrl(action.url || "");
    setRenderAs(action.render_as || "secondary_button");
    setStatus(action.status || "suggested");
  }, [action]);

  const urlError = !isSafeUrl(url) ? "Ogiltigt URL-format" : "";

  const save = async () => {
    if (urlError) {
      toast.error(urlError);
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions/${encodeURIComponent(action.action_id)}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({
            label_override: label,
            url,
            render_as: renderAs,
            status,
          }),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || data.error || "Misslyckades");
      toast.success("Sparat");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.message || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  const toggleLink = async () => {
    setLinking(true);
    try {
      const path = action.linked_to_blueprint ? "unlink" : "link";
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions/${encodeURIComponent(action.action_id)}/${path}`,
        {
          method: "POST",
          credentials: "include",
          headers: { ...getAuthHeaders() },
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || data.error || "Misslyckades");
      toast.success(action.linked_to_blueprint ? "Bortkopplad från blueprint" : "Kopplad till blueprint");
      onChanged?.();
    } catch (e) {
      toast.error(e.message || "Kunde inte ändra koppling");
    } finally {
      setLinking(false);
    }
  };

  // Compute clear status pill
  let pill = null;
  if (action.runtime_eligible) {
    pill = (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <CheckCircle2 className="w-3 h-3" /> Aktiv i agenten
      </span>
    );
  } else if (action.status === "approved" && !action.linked_to_blueprint) {
    pill = (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
        <AlertCircle className="w-3 h-3" /> Ej kopplad
      </span>
    );
  } else if (action.linked_to_blueprint && action.status !== "approved") {
    pill = (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
        <XCircle className="w-3 h-3" /> Blockerad
      </span>
    );
  } else if (action.status === "disabled") {
    pill = (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
        Inaktiverad
      </span>
    );
  } else {
    pill = <StatusBadge value={action.status} />;
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`action-row-${action.action_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs font-mono px-1.5 py-0.5 bg-gray-100 rounded text-gray-700">{action.action_id}</code>
            {pill}
            <span className="text-[10px] text-gray-500">render: {action.render_as}</span>
            {action.template_id && (
              <span className="text-[10px] text-gray-400">template: {action.template_id}</span>
            )}
          </div>
          <div className="mt-2 text-sm font-medium text-gray-900">
            {action.label_override || action.action_id}
          </div>
          {action.url ? (
            <a href={action.url} target="_blank" rel="noopener noreferrer"
               className="text-xs text-[#255E69] hover:underline break-all">
              {action.url}
            </a>
          ) : (
            <p className="text-xs text-gray-400 italic">Ingen URL satt</p>
          )}
          {action.evidence && (
            <p className="text-[11px] text-gray-500 mt-1 italic">{action.evidence}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <Button
              size="sm" variant="outline"
              onClick={() => setEditing((e) => !e)}
              data-testid={`action-edit-${action.action_id}`}
            >
              {editing ? "Stäng" : "Redigera"}
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={toggleLink}
              disabled={linking}
              data-testid={`action-toggle-link-${action.action_id}`}
            >
              {linking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : action.linked_to_blueprint
                  ? <Unlink className="w-3.5 h-3.5 mr-1" />
                  : <LinkIcon className="w-3.5 h-3.5 mr-1" />}
              {action.linked_to_blueprint ? "Ta bort koppling" : "Koppla till blueprint"}
            </Button>
          </div>
          <ActionPreview action={action} />
        </div>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="md:col-span-2">
            <Label className="text-xs">Etikett</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid={`action-edit-label-${action.action_id}`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid={`action-edit-url-${action.action_id}`} />
            <p className="text-[11px] text-gray-500 mt-1">{URL_SCHEME_HELP}</p>
            {urlError && <p className="text-[11px] text-red-600 mt-1">{urlError}</p>}
          </div>
          <div>
            <Label className="text-xs">Render-stil</Label>
            <select
              value={renderAs} onChange={(e) => setRenderAs(e.target.value)}
              className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid={`action-edit-render-${action.action_id}`}
            >
              {RENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid={`action-edit-status-${action.action_id}`}
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 flex items-center justify-end">
            <Button size="sm" onClick={save} disabled={saving || !!urlError} data-testid={`action-save-${action.action_id}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Spara
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApprovedActionsManager({ clientId, isBlueprint = null }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ actions: [], blueprint_approved_action_ids: [] });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions`,
        { credentials: "include", headers: getAuthHeaders() }
      );
      if (!r.ok) throw new Error(`Status ${r.status}`);
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error("Kunde inte hämta actions");
    } finally {
      setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const actions = data.actions || [];
  const eligible = actions.filter((a) => a.runtime_eligible).length;
  const linkedNotApproved = actions.filter((a) => a.linked_to_blueprint && a.status !== "approved").length;
  const approvedNotLinked = actions.filter((a) => a.status === "approved" && !a.linked_to_blueprint).length;

  return (
    <div className="space-y-4" data-testid="approved-actions-manager">
      {/* H3.3 — Source-of-truth banner (LIVE/UNDERLAG) för actions */}
      {isBlueprint !== null && (
        <div
          className={`border rounded-lg p-3 text-sm flex items-start gap-2 ${
            isBlueprint
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-sky-50 border-sky-200 text-sky-800"
          }`}
          data-testid="actions-source-banner"
        >
          <span
            data-testid="actions-source-state"
            data-source-state={isBlueprint ? "live" : "underlag"}
            className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wider rounded-full px-2 py-0.5 border ${
              isBlueprint
                ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                : "bg-sky-50 text-sky-700 border-sky-300"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isBlueprint ? "bg-emerald-500" : "bg-sky-500"
              }`}
            />
            {isBlueprint ? "LIVE" : "UNDERLAG"}
          </span>
          <span>
            {isBlueprint ? (
              <>Blueprint-runtime aktiv — kopplade och godkända actions renderas i chatten just nu.</>
            ) : (
              <>Legacy-runtime aktiv — knappar & länkar här är <strong>underlag</strong>. De renderas i chatten först när blueprint-runtime aktiveras för kunden.</>
            )}
          </span>
        </div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Aktiv i agenten</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">{eligible}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Approved, ej kopplad</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">{approvedNotLinked}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-red-700 font-semibold">Kopplad, ej approved</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{linkedNotApproved}</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-600 font-semibold">Totalt</div>
          <div className="text-2xl font-bold text-gray-700 mt-1">{actions.length}</div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-900">Knappar & länkar</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Två krav för att en knapp ska visas i agenten: status <code>approved</code> + kopplad till blueprint.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="actions-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => setShowNew((s) => !s)} data-testid="actions-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Ny action
          </Button>
        </div>
      </div>

      {/* New action form */}
      {showNew && (
        <NewActionForm
          clientId={clientId}
          onCreated={() => { setShowNew(false); load(); }}
          onCancel={() => setShowNew(false)}
        />
      )}

      {/* Rows */}
      {actions.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-10 text-center">
          <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Inga actions ännu. Lägg till första knappen via "Ny action".</p>
        </div>
      ) : (
        <div className="space-y-2">
          {actions.map((a) => (
            <ActionRow key={a.action_id} action={a} clientId={clientId} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
